import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Package,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Home,
  Loader2,
  Grid,
  List,
  X,
} from "lucide-react";
import WebsiteHeader from "./WebsiteHeader";
import WebsiteFooter from "./WebsiteFooter";

const API = process.env.REACT_APP_BACKEND_URL;

const WebsiteProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [department, setDepartment] = useState(searchParams.get("department") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  
  const [page, setPage] = useState(0);
  const limit = 24;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString(),
      });
      
      if (search) params.append("search", search);
      if (department && department !== "all") params.append("department", department);
      if (category && category !== "all") params.append("category", category);
      if (inStockOnly) params.append("in_stock_only", "true");
      
      const response = await axios.get(`${API}/api/website/products?${params.toString()}`);
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, [page, search, department, category, inStockOnly]);

  const fetchFilters = useCallback(async () => {
    try {
      const [deptRes, catRes] = await Promise.all([
        axios.get(`${API}/api/website/departments`),
        axios.get(`${API}/api/website/categories${department ? `?department=${department}` : ""}`),
      ]);
      setDepartments(deptRes.data.departments || []);
      setCategories(catRes.data.categories || []);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  }, [department]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchProducts();
  };

  const clearFilters = () => {
    setSearch("");
    setDepartment("");
    setCategory("");
    setInStockOnly(false);
    setPage(0);
    setSearchParams({});
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(price || 0);
  };

  const totalPages = Math.ceil(total / limit);
  const hasFilters = search || department || category || inStockOnly;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <WebsiteHeader />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/site" className="text-gray-500 hover:text-blue-600 flex items-center gap-1">
              <Home className="h-4 w-4" />
              Home
            </Link>
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <span className="text-gray-800 font-medium">Products</span>
            {department && (
              <>
                <ChevronRight className="h-4 w-4 text-gray-400" />
                <span className="text-gray-800">{department}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 flex-1">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {department || "All Products"}
          </h1>
          <p className="text-gray-600">
            {total.toLocaleString()} products available
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <Card className="sticky top-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Filters
                  </h2>
                  {hasFilters && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearFilters}
                      className="text-red-600 hover:text-red-700 h-auto p-1"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Search */}
                <form onSubmit={handleSearch} className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search products..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </form>

                {/* Department Filter */}
                <div className="mb-4">
                  <label className="text-sm font-medium mb-2 block">Department</label>
                  <Select value={department} onValueChange={(v) => { setDepartment(v); setCategory(""); setPage(0); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {[...departments].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((dept) => (
                        <SelectItem key={dept.name} value={dept.name}>
                          {dept.name} ({dept.count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Filter */}
                {categories.length > 0 && (
                  <div className="mb-4">
                    <label className="text-sm font-medium mb-2 block">Category</label>
                    <Select value={category} onValueChange={(v) => { setCategory(v); setPage(0); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                          <SelectItem key={cat.name} value={cat.name}>
                            {cat.name} ({cat.count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* In Stock Filter */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="inStock"
                    checked={inStockOnly}
                    onChange={(e) => { setInStockOnly(e.target.checked); setPage(0); }}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="inStock" className="text-sm">In stock only</label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* View Controls */}
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className="p-2"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="p-2"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <Package className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No products found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters or search term</p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map((product) => (
                  <Card key={product.id} className="group hover:shadow-lg transition-all duration-300 overflow-hidden">
                    <CardContent className="p-4">
                      <div className="aspect-square bg-white rounded-lg mb-3 flex items-center justify-center overflow-hidden border">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="max-w-full max-h-full object-contain p-1"
                          />
                        ) : (
                          <Package className="h-12 w-12 text-gray-300" />
                        )}
                      </div>
                      <h3 className="font-medium text-sm line-clamp-2 mb-2 min-h-[2.5rem] text-gray-800">
                        {product.name}
                      </h3>
                      <p className="text-xs text-gray-500 mb-2">PLU: {product.plu_code}</p>
                      <div className="flex justify-between items-center">
                        <p className="text-lg font-bold text-blue-600">
                          {formatPrice(product.selling_price_incl_vat)}
                        </p>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            product.stock_status === "In Stock" 
                              ? "bg-green-50 text-green-700 border-green-200" 
                              : product.stock_status === "Low Stock"
                                ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                : "bg-red-50 text-red-700 border-red-200"
                          }`}
                        >
                          {product.stock_status}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {products.map((product) => (
                  <Card key={product.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 flex gap-4">
                      <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="max-w-full max-h-full object-contain p-1"
                          />
                        ) : (
                          <Package className="h-8 w-8 text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-gray-800 mb-1">{product.name}</h3>
                            <p className="text-xs text-gray-500">PLU: {product.plu_code}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-blue-600">
                              {formatPrice(product.selling_price_incl_vat)}
                            </p>
                            <Badge 
                              variant="outline" 
                              className={`text-xs mt-1 ${
                                product.stock_status === "In Stock" 
                                  ? "bg-green-50 text-green-700 border-green-200" 
                                  : "bg-red-50 text-red-700 border-red-200"
                              }`}
                            >
                              {product.stock_status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm text-gray-600">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <WebsiteFooter />
    </div>
  );
};

export default WebsiteProducts;
